import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Input, Loading, NumberInput, Segmented, Select, Textarea } from '../components/ui.jsx';
import { EntityPicker } from '../components/EntityPicker.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { barrelsFromLiters, liters, money, today } from '../lib/format.js';

const blank = {
  sold_on: today(),
  customer_id: null,
  customer_name: '',
  unit: 'barrel',
  quantity: '',
  liters: '',
  tank_id: '',
  purchase_id: null,
  currency: 'IQD',
  price_per_liter: '',
  payment_type: 'debt',
  notes: '',
};

export default function SaleForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const customers = useApi('/api/customers');
  const tanks = useApi('/api/tanks');
  const purchases = useApi('/api/purchases', { limit: 500 });
  const existing = useApi(isEdit ? `/api/sales/${id}` : null, null, { enabled: isEdit });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!existing.data) return;
    const s = existing.data;
    setForm({
      sold_on: s.sold_on,
      customer_id: s.customer_id,
      customer_name: s.customer_name,
      unit: s.unit,
      quantity: s.quantity,
      liters: s.liters,
      tank_id: String(s.tank_id),
      purchase_id: s.purchase_id ? String(s.purchase_id) : null,
      currency: s.currency,
      price_per_liter: Number(s.price_per_liter) ? s.price_per_liter : '',
      payment_type: s.payment_type,
      notes: s.notes || '',
    });
  }, [existing.data]);

  // Litres is what the operator actually measures (a fuel meter reads litres,
  // not barrels), so for barrel sales it is the field they type; the barrel
  // count is derived from it for display and for the record. Tonnes do not
  // convert exactly, so those litres stay hand-entered independently.
  const barrelsOut = form.unit === 'barrel' ? barrelsFromLiters(form.liters) : form.quantity;

  const selectedTank = useMemo(
    () => (tanks.data || []).find((tank) => String(tank.id) === String(form.tank_id)),
    [tanks.data, form.tank_id],
  );
  const selectedCustomer = useMemo(
    () => (customers.data || []).find((c) => c.id === form.customer_id),
    [customers.data, form.customer_id],
  );
  const selectedPurchase = useMemo(
    () => (purchases.data?.rows || []).find((p) => String(p.id) === String(form.purchase_id)),
    [purchases.data, form.purchase_id],
  );

  const available = selectedTank
    ? Number(selectedTank.current_liters) +
      (isEdit && existing.data && existing.data.tank_id === selectedTank.id ? Number(existing.data.liters) : 0)
    : null;
  const shortfall = available !== null && Number(form.liters) > available;

  // The amount charged to the customer is always liters * price_per_liter -
  // shown here for confirmation before saving, computed exactly by the server.
  const computedAmount = useMemo(() => {
    const litersNum = Number(form.liters);
    const price = Number(form.price_per_liter);
    return litersNum > 0 && price > 0 ? litersNum * price : null;
  }, [form.liters, form.price_per_liter]);

  const canSave =
    form.sold_on &&
    (form.customer_id || form.customer_name) &&
    Number(form.liters) > 0 &&
    (form.unit === 'barrel' || Number(form.quantity) > 0) &&
    form.tank_id &&
    Number(form.price_per_liter) > 0 &&
    !shortfall;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      ...form,
      tank_id: Number(form.tank_id),
      quantity: form.unit === 'barrel' ? barrelsOut : form.quantity,
    };
    try {
      if (isEdit) {
        await api.patch(`/api/sales/${id}`, payload);
        toast.success(t.sale.updated);
        navigate(`/sell/${id}/receipt`, { replace: true });
      } else {
        const created = await api.post('/api/sales', payload, {
          queueOffline: true,
          label: `${t.dashboard.sale} — ${form.customer_name || selectedCustomer?.name || t.sale.customer}`,
        });
        if (created.queued) {
          toast.warn(t.sale.queued);
          navigate('/sell', { replace: true });
        } else {
          toast.success(t.sale.saved(created.receipt_no));
          navigate(`/sell/${created.id}/receipt`, { replace: true });
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && existing.loading && !existing.data) return <Loading />;

  return (
    <>
      <PageHeader
        title={isEdit ? t.sale.editTitle : t.sale.newTitle}
        subtitle={t.sale.formSubtitle}
        back="/sell"
      />

      <form className="page" onSubmit={submit}>
        {error && <Alert tone="error">{error}</Alert>}

        <Card title={t.sale.customer}>
          <EntityPicker
            label={t.sale.customer}
            required
            items={customers.data || []}
            value={form.customer_id}
            name={form.customer_name}
            onChange={({ id: customerId, name }) => set({ customer_id: customerId, customer_name: name })}
            createLabel={t.sale.addCustomer}
            hint={t.sale.customerHint}
          />
          {selectedCustomer &&
            (Number(selectedCustomer.balance_iqd) !== 0 || Number(selectedCustomer.balance_usd) !== 0) && (
              <Alert tone="warn">
                {t.sale.customerOwes(selectedCustomer.name, money(selectedCustomer.balance_iqd, 'IQD'))}
                {Number(selectedCustomer.balance_usd) !== 0 && ` و${money(selectedCustomer.balance_usd, 'USD')}`}.
              </Alert>
            )}
          <Input
            label={t.common.date}
            required
            type="date"
            value={form.sold_on}
            onChange={(e) => set({ sold_on: e.target.value })}
          />
        </Card>

        <Card title={t.common.quantity}>
          <Segmented
            label={t.sale.soldBy}
            value={form.unit}
            onChange={(unit) => set({ unit, quantity: '', liters: '' })}
            options={[
              { value: 'barrel', label: t.sale.barrel },
              { value: 'ton', label: t.sale.ton },
            ]}
            hint={t.sale.barrelHint}
          />
          <div className="form-grid cols-2">
            <NumberInput
              label={t.purchase.liters}
              required
              value={form.liters}
              onChange={(litres) => set({ liters: litres })}
              placeholder="0.000"
              hint={form.unit === 'ton' ? t.purchase.litersHint : undefined}
            />
            {form.unit === 'barrel' ? (
              <Input label={t.sale.barrels} value={barrelsOut ? String(barrelsOut) : ''} readOnly hint={t.sale.barrelsCalculated} />
            ) : (
              <NumberInput
                label={t.sale.tonnes}
                required
                value={form.quantity}
                onChange={(quantity) => set({ quantity })}
                placeholder="0"
              />
            )}
          </div>

          <Select
            label={t.sale.sourceTank}
            required
            placeholder={t.purchase.chooseTank}
            value={form.tank_id}
            onChange={(e) => set({ tank_id: e.target.value })}
            options={(tanks.data || []).map((tank) => ({
              value: tank.id,
              label: `${tank.name} — ${liters(tank.current_liters)}`,
            }))}
            hint={available !== null ? t.sale.available(liters(available)) : undefined}
          />
          {shortfall && <Alert tone="error">{t.sale.shortfall(selectedTank.name, liters(available))}</Alert>}

          <Select
            label={t.sale.batch}
            placeholder={t.sale.noBatch}
            value={form.purchase_id || ''}
            onChange={(e) => set({ purchase_id: e.target.value || null })}
            options={(purchases.data?.rows || []).map((p) => ({ value: p.id, label: p.batch_no }))}
            hint={t.sale.batchOptional}
          />
          {selectedPurchase && String(selectedPurchase.tank_id) !== String(form.tank_id) && (
            <Alert tone="warn">{t.sale.batchTankMismatch(selectedPurchase.batch_no)}</Alert>
          )}
        </Card>

        <Card title={t.purchase.price}>
          <Segmented
            label={t.shared.pricedIn}
            value={form.currency}
            onChange={(currency) => set({ currency })}
            options={[
              { value: 'IQD', label: t.report.iqd },
              { value: 'USD', label: t.report.usd },
            ]}
            hint={t.shared.currencyHint}
          />
          <NumberInput
            label={t.sale.pricePerLiter}
            required
            value={form.price_per_liter}
            onChange={(price_per_liter) => set({ price_per_liter })}
            placeholder="0"
            hint={t.sale.pricePerLiterHint}
          />
          {computedAmount !== null && (
            <Alert tone="info">{t.purchase.computedAmount(money(computedAmount, form.currency))}</Alert>
          )}
          <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
        </Card>

        <div className="btn-row end no-print">
          <button type="button" className="btn" onClick={() => navigate('/sell')} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={!canSave || busy}>
            {busy ? <span className="spinner" /> : isEdit ? t.common.saveChanges : t.sale.save}
          </button>
        </div>
      </form>
    </>
  );
}
